import { useState, useCallback } from "react";
import { PDFDocument, degrees } from "pdf-lib";
import { getDocument, GlobalWorkerOptions, PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';

declare global {
  interface Window {
    pdfjsWorker: any;
  }
}

// Configure PDF.js worker
const pdfjsVersion = '3.11.174'; // Specify a fixed version for stability
GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsVersion}/pdf.worker.min.js`;
import { InteractivePreview } from './InteractivePreview';

import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { FileUpload } from "./FileUpload";
import { PDFPreview } from "./PDFPreview";
import { FileText, Image as ImageIcon, Download, RotateCw, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";

export const PDFStamper = () => {
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(0.15);
  const [processing, setProcessing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [modifiedPdfBytes, setModifiedPdfBytes] = useState<Uint8Array | null>(null);

  const handlePDFSelect = (files: File[]) => {
    const file = files[0]; // Only take the first file for PDF
    if (file.type !== "application/pdf") {
      toast.error("Please select a PDF file");
      return;
    }
    setPdfFile(file);
    toast.success("PDF loaded successfully");
  };

  const handleImageSelect = useCallback((files: File[]) => {
    const validFiles = files.filter(file => file.type.startsWith("image/png"));
    if (validFiles.length === 0) {
      toast.error("Please select at least one PNG image");
      return;
    }
    setImageFiles(prev => [...prev, ...validFiles]);
    toast.success(`Added ${validFiles.length} image(s) successfully`);
  }, []);

  const processPdf = async (directDownload: boolean = false): Promise<Uint8Array | null> => {
    if (!pdfFile || imageFiles.length === 0) {
      toast.error("Please select a PDF and at least one image file");
      return null;
    }
    
    setProcessing(true);
    
    try {
      const pdfBytes = await pdfFile.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      
      // Embed all selected images
      const pngImages = await Promise.all(
        imageFiles.map(async (file: File) => {
          const buffer = await file.arrayBuffer();
          const image = await pdfDoc.embedPng(buffer);
          return image;
        })
      );

      const pages = pdfDoc.getPages();
      
      // Process each page
      for (const page of pages) {
        const { width, height } = page.getSize();
        
        // Select one random image for this page
        const randomImage = pngImages[Math.floor(Math.random() * pngImages.length)];
        
        if (randomImage) {
          const imageDims = randomImage.scale(scale);
          
          // Calculate position in lower right quadrant
          const minX = width / 2 + 10;
          const minY = 10;
          const maxX = width - imageDims.width - 10;
          const maxY = (height / 2) - imageDims.height - 10;
          
          // Random position within lower right quadrant
          const xPosition = minX + Math.random() * (maxX - minX);
          const yPosition = minY + Math.random() * (maxY - minY);
          
          page.drawImage(randomImage, {
            x: xPosition,
            y: yPosition,
            width: imageDims.width,
            height: imageDims.height,
            rotate: degrees(rotation + (Math.random() * 10 - 5)),
          });
        }
      }

      const modifiedBytes = await pdfDoc.save();
      
      if (directDownload) {
        const blob = new Blob([modifiedBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `stamped_${pdfFile.name || 'document'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('PDF downloaded successfully!');
        return null;
      }
      
      return modifiedBytes;
      
    } catch (error) {
      console.error("Error processing PDF:", error);
      toast.error("Failed to process PDF");
      return null;
    } finally {
      setProcessing(false);
    }
  };

  const handleDirectDownload = async () => {
    await processPdf(true);
  };

  const handlePreview = async () => {
    const result = await processPdf(false);
    if (result) {
      setModifiedPdfBytes(result);
      setShowPreview(true);
    }
  };

  const handleSave = async (pdfBytes: Uint8Array) => {
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `stamped_${pdfFile?.name || 'document'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
    setShowPreview(false);
    toast.success('PDF downloaded successfully!');
  };

  // Show interactive preview if enabled
  if (showPreview && pdfFile) {
    return (
      <div className="min-h-screen bg-background">
        <InteractivePreview
          pdfBytes={modifiedPdfBytes}
          pdfFile={pdfFile}
          imageFiles={imageFiles}
          scale={scale}
          rotation={rotation}
          onSave={handleSave}
          onCancel={() => setShowPreview(false)}
        />
      </div>
    );
  }

  // Show the main form
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold text-foreground">PDF Image Stamper</h1>
          <p className="text-muted-foreground mt-2">Add PNG images to the lower half of your PDF pages</p>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <FileUpload
            accept=".pdf"
            onFileSelect={handlePDFSelect}
            label="Select PDF File"
            icon={<FileText className="w-6 h-6" />}
            multiple={false}
          />
          <FileUpload
            accept="image/png"
            onFileSelect={handleImageSelect}
            icon={<ImageIcon className="h-6 w-6" />}
            label="Upload PNG Images"
            description="Select one or more PNG images to stamp on the PDF"
            multiple={true}
          />
        </div>

        {pdfFile && imageFiles.length > 0 && (
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 text-foreground">Preview (First Page)</h2>
              <PDFPreview 
                pdfFile={pdfFile} 
                imageFile={imageFiles[0]} 
                rotation={rotation}
                scale={scale}
              />
            </Card>

            <Card className="p-6 space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Maximize2 className="w-5 h-5 text-primary" />
                    <label className="font-medium text-foreground">Image Size</label>
                  </div>
                  <span className="text-sm font-medium text-primary">{Math.round(scale * 100)}%</span>
                </div>
                <Slider
                  value={[scale * 100]}
                  onValueChange={(value) => setScale(value[0] / 100)}
                  min={5}
                  max={50}
                  step={1}
                  className="w-full"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <RotateCw className="w-5 h-5 text-primary" />
                    <label className="font-medium text-foreground">Image Rotation</label>
                  </div>
                  <span className="text-sm font-medium text-primary">{rotation}°</span>
                </div>
                <Slider
                  value={[rotation]}
                  onValueChange={(value) => setRotation(value[0])}
                  min={0}
                  max={360}
                  step={1}
                  className="w-full"
                />
              </div>

              <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-primary" />
                  <span className="text-foreground font-medium">PDF:</span>
                  <span className="text-muted-foreground truncate">{pdfFile.name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <ImageIcon className="w-4 h-4 text-primary" />
                  <span className="text-foreground font-medium">Images:</span>
                  <span className="text-muted-foreground truncate">{imageFiles.length} images selected</span>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex flex-col sm:flex-row gap-2 w-full">
                  <Button 
                    onClick={handleDirectDownload}
                    disabled={!pdfFile || imageFiles.length === 0 || processing}
                    variant="outline"
                    className="w-full sm:w-1/2"
                  >
                    {processing ? 'Processing...' : 'Download Directly'}
                  </Button>
                  <Button 
                    onClick={handlePreview}
                    disabled={!pdfFile || imageFiles.length === 0 || processing}
                    className="w-full sm:w-1/2"
                  >
                    {processing ? 'Loading...' : 'Preview & Edit'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {(!pdfFile || imageFiles.length === 0) && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Upload both PDF and PNG files to get started</p>
          </div>
        )}
      </main>
    </div>
  );
};
